<script setup lang="ts">
import { RadioGroupItem, RadioGroupRoot } from 'reka-ui'

const localePath = useLocalePath()

const { t } = useI18n()

enum PaymentType {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

const paymentType = [
  {
    name: t('pricing.monthly.title'),
    id: PaymentType.MONTHLY,
  },
  {
    name: t('pricing.oneTime.title'),
    id: PaymentType.YEARLY,
  },
]

const plans = [
  {
    name: t('pricing.firstBranch'),
    price: {
      monthly: {
        installation: t('pricing.free'),
        subscription: '1 549 Kč',
      },
      yearly: {
        installation: '25 900 Kč',
        subscription: '300 Kč',
      },
    },
  },
  {
    name: t('pricing.secondBranch'),
    price: {
      monthly: {
        installation: t('pricing.free'),
        subscription: '1 199 Kč',
      },
      yearly: {
        installation: '18 900 Kč',
        subscription: '300 Kč',
      },
    },
  },
  {
    name: t('pricing.thirdBranch'),
    price: {
      monthly: {
        installation: t('pricing.free'),
        subscription: '1 099 Kč',
      },
      yearly: {
        installation: '17 900 Kč',
        subscription: '300 Kč',
      },
    },
  },
  {
    name: t('pricing.fourthBranch'),
    price: {
      monthly: {
        installation: t('pricing.free'),
        subscription: '999 Kč',
      },
      yearly: {
        installation: '16 900 Kč',
        subscription: '300 Kč',
      },
    },
  },
  {
    name: t('pricing.fifthAndMore'),
    price: {
      monthly: {
        installation: t('pricing.free'),
        subscription: t('pricing.individually'),
      },
      yearly: {
        installation: t('pricing.individually'),
        subscription: '300 Kč',
      },
    },
  },
]

const includedItems = [
  t('pricing.included.users'),
  t('pricing.included.updates'),
  t('pricing.included.anywhere'),
  t('pricing.included.eVouchers'),
  t('pricing.included.support'),
]

const selectedId = ref(PaymentType.MONTHLY)
</script>

<template>
  <UiSection id="cenik" :title="$t('pricing.title')">
    <div>
      <h3
        v-if="selectedId === PaymentType.MONTHLY"
        class="mb-8 text-center text-3xl lg:text-5xl font-semibold"
      >
        {{ $t('pricing.monthly.subtitle') }}
      </h3>
      <h3
        v-else-if="selectedId === PaymentType.YEARLY"
        class="mb-8 text-center text-3xl lg:text-5xl font-semibold"
      >
        {{ $t('pricing.oneTime.subtitle') }}
      </h3>
      <p
        v-if="selectedId === PaymentType.MONTHLY"
        class="mx-auto mb-16 max-w-2xl text-center text-lg"
      >
        {{ $t('pricing.monthly.text') }}
      </p>
      <p
        v-else-if="selectedId === PaymentType.YEARLY"
        class="mx-auto mb-16 max-w-2xl text-center text-lg"
      >
        {{ $t('pricing.oneTime.text') }}
      </p>

      <div class="sticky top-16 w-full px-4 pb-8 z-10">
        <div class="mx-auto w-full max-w-md">
          <RadioGroupRoot v-model="selectedId" class="mx-auto flex w-fit space-x-1 rounded-full bg-neutral-300 p-1">
            <RadioGroupItem
              v-for="plan in paymentType"
              :key="plan.id"
              :value="plan.id"
              :class="[
                selectedId === plan.id && plan.id === PaymentType.MONTHLY
                  ? 'bg-optilynx-pink! text-white hover:bg-optilynx-pink-hover!'
                  : '',
                selectedId === plan.id && plan.id === PaymentType.YEARLY
                  ? 'bg-optilynx-purple! text-white hover:bg-optilynx-purple-hover!'
                  : '',
                selectedId !== plan.id ? 'bg-white hover:bg-neutral-50 text-gray-900' : '',
              ]"
              class="relative flex cursor-pointer rounded-full px-5 py-2 focus:outline-hidden font-medium text-sm"
            >
              {{ plan.name }}
            </RadioGroupItem>
          </RadioGroupRoot>
        </div>
      </div>

      <div
        class="mx-auto grid grid-cols-1 gap-5 text-black sm:grid-cols-2 md:grid-cols-3"
      >
        <div
          v-for="(plan, index) in plans"
          class="overflow-hidden rounded-2xl p-8 text-white relative"
          :class="{
            'sm:col-span-2': index === plans.length - 1,
            'bg-optilynx-pink': selectedId === PaymentType.MONTHLY,
            'bg-optilynx-purple': selectedId === PaymentType.YEARLY,
          }"
        >
          <span v-if="index < 4" class="absolute right-1 -bottom-6 opacity-50 text-[150px] leading-[1] italic" style="background-image: '';">{{ index + 1 }}</span>
          <span v-else class="absolute right-1 -bottom-6 opacity-50 text-[150px] leading-[1] italic" style="background-image: '';">{{ index + 1 }}+</span>
          <p class="mb-2 w-fit text-xl">{{ plan.name }}</p>
          <div v-if="selectedId === PaymentType.MONTHLY">
            <div class="mb-2">
              <p>{{ $t('pricing.fees.instalation') }}</p>
              <p class="text-2xl font-bold capitalize">
                {{ plan.price.monthly.installation }}
              </p>
            </div>
            <div>
              <p>{{ $t('pricing.fees.subscription') }}</p>
              <p class="text-2xl font-bold capitalize">
                {{ plan.price.monthly.subscription }}
              </p>
            </div>
          </div>
          <div v-if="selectedId === PaymentType.YEARLY">
            <div class="mb-2">
              <p>{{ $t('pricing.fees.instalation') }}</p>
              <p class="text-2xl font-bold capitalize">
                {{ plan.price.yearly.installation }}
              </p>
            </div>
            <div>
              <p>{{ $t('pricing.fees.operating') }}</p>
              <p class="text-2xl font-bold capitalize">
                {{ plan.price.yearly.subscription }}
                <span class="text-sm lowercase">{{ $t('pricing.monthly.unit') }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- z-20 keeps the sticky payment-type toggle (z-10) behind this card as it scrolls past -->
      <div class="relative z-20 mt-8 rounded-2xl bg-white p-8">
        <h3 class="mb-6 text-center text-2xl font-medium">{{ $t('pricing.included.title') }}</h3>
        <ul class="mx-auto grid max-w-4xl gap-3 sm:grid-cols-2">
          <li v-for="item in includedItems" :key="item" class="flex items-start gap-3">
            <Icon name="fa6-solid:check" class="mt-1.5 h-4 w-4 shrink-0 text-optilynx-pink" />
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>

      <p class="mt-8 text-center text-sm text-neutral-500">
        {{ $t('pricing.vatNote') }}
      </p>
    </div>
  </UiSection>
</template>
